/**
 * Named paragraph styles: define how a heading looks once, and update every one of them to match.
 *
 * WHAT IT REPLACES. Restyling a procedure today means selecting each heading and changing it, which
 * for a sixty-page SOP is an afternoon and a guarantee that three of them end up different. Worse,
 * the difference is invisible until somebody prints it: nothing in the document records that these
 * forty paragraphs were meant to look alike.
 *
 * THE DEFINITION LIVES ON THE DOCUMENT, not on the tenant. A controlled procedure has to render the
 * same a year later; if its appearance came from a stylesheet somebody else could edit, a released
 * revision could change its look without a revision, without an approval, and without an entry in
 * its trail. That is the one thing a controlled document may not do. The cost -- two procedures in
 * one tenant can look different -- is the right cost, and it is the same decision page setup made.
 *
 * NOTHING IS BAKED INTO THE TEXT. Applying a style does not write a font size onto a paragraph; the
 * paragraph is a heading, and what a heading looks like is worked out at display time, at both ends.
 * Store the appearance on the block and "update every one of them" becomes impossible, which is the
 * whole feature. Same rule as the heading numbers and the cross-references, for the same reason.
 *
 * DIRECT FORMATTING STILL WINS. A paragraph somebody has made red stays red: a style is the floor,
 * not a cage. That is how every editor anybody has used behaves, and a style that overrode a
 * deliberate choice would be a style people stop using.
 *
 * MIRRORED IN document_rendition.py. The screen and the signed PDF read the same numbers; a style
 * that applies in one and not the other is worse than no styles at all.
 */

/** The styles a document has. Fixed: a style people can create is a style nobody else recognises. */
export const STYLE_NAMES = ["normal", "h1", "h2", "h3", "h4", "h5", "h6"] as const;
export type StyleName = (typeof STYLE_NAMES)[number];

export const STYLE_LABELS: Record<StyleName, string> = {
    normal: "Normal text",
    h1: "Heading 1", h2: "Heading 2", h3: "Heading 3",
    h4: "Heading 4", h5: "Heading 5", h6: "Heading 6",
};

/**
 * What a style says.
 *
 * EVERY DEFAULT NAMES EVERY FIELD, deliberately. A sheet that omits what it does not change reads
 * well and compares badly: two styles that say the same thing came back as different objects
 * depending on whether the document stored them, which is how "has this been restyled" got its
 * first wrong answer. The fields stay optional in the TYPE because a document may carry a partial
 * one; styleOf fills it in.
 */
export type ParagraphStyle = {
    /** Points. */
    size?: number;
    /** 400 or 700 — the two a procedure uses. */
    weight?: number;
    italic?: boolean;
    /** A CSS colour, or absent for the body ink. */
    colour?: string;
    /** Points above and below the paragraph. */
    spaceBefore?: number;
    spaceAfter?: number;
    /** A multiplier, as the line-height control states it. */
    lineHeight?: number;
    /** ALL CAPS, which a procedure's lowest heading levels often are. */
    caps?: boolean;
    /** A rule beneath, which is how a section head has been drawn for thirty years. */
    underlined?: boolean;
};

export type StyleSheet = Record<StyleName, ParagraphStyle>;

/**
 * What a document looks like before anybody changes it.
 *
 * THESE ARE THE VALUES THE PAPER ALREADY DREW, moved here rather than invented: a document that has
 * never had a style touched has to render exactly as it did before this existed, in the editor and
 * in the PDF. A default that looks "better" is a silent restyle of every procedure in the tenant.
 *
 * A SECTION HEAD IN A PROCEDURE IS SMALL, BOLD AND RULED OFF -- not a magazine headline -- and the
 * lower levels step down in WEIGHT rather than in size, because by the fourth level the distinction
 * being drawn is small and four sizes of bold in one document is noise.
 */
const PLAIN = { italic: false, colour: undefined, caps: false, underlined: false,
                // THE BODY'S LEADING, ON HEADINGS TOO. The paper never set a line height on a
                // heading, so they have always inherited this one; naming it here changes nothing
                // on screen and makes the sheet say what it is rather than what it omits.
                lineHeight: 1.5 };

export const DEFAULT_STYLES: StyleSheet = {
    normal: { ...PLAIN, size: 11, weight: 400, spaceBefore: 0, spaceAfter: 10 },
    h1: { ...PLAIN, size: 11, weight: 700, spaceBefore: 16, spaceAfter: 6, underlined: true },
    h2: { ...PLAIN, size: 10.5, weight: 700, spaceBefore: 14, spaceAfter: 5 },
    h3: { ...PLAIN, size: 10, weight: 700, spaceBefore: 12, spaceAfter: 4, italic: true },
    h4: { ...PLAIN, size: 10, weight: 700, spaceBefore: 10, spaceAfter: 3 },
    h5: { ...PLAIN, size: 10, weight: 600, spaceBefore: 10, spaceAfter: 3, italic: true },
    h6: { ...PLAIN, size: 9.5, weight: 600, spaceBefore: 10, spaceAfter: 3, caps: true },
};

const NUMBER = (value: any, fallback: number, low: number, high: number) => {
    const parsed = parseFloat(String(value));
    // CLAMPED, NOT REFUSED. A style is edited by capturing whatever the caret was sitting in, and a
    // paragraph at 400 points is a typo somebody made in the font-size box -- it should give them a
    // large heading, not an error, and not a document that cannot be opened.
    return Number.isFinite(parsed) ? Math.min(high, Math.max(low, parsed)) : fallback;
};

/**
 * One style, read out of whatever the document is carrying, with every field made safe.
 *
 * THE SAME SHAPE WHATEVER IT IS GIVEN, including null. An early return of the raw defaults looked
 * harmless and was not: the defaults name only the fields they change, so a document carrying
 * nothing answered `{size, weight, spaceAfter}` while a document carrying the identical style
 * answered those plus `lineHeight: 1.5` and `caps: undefined`. Anything comparing the two -- which
 * is how "has this been restyled" is decided -- then said yes about a document nobody had touched.
 */
export function styleOf(stored: any, name: StyleName): ParagraphStyle {
    const fallback = DEFAULT_STYLES[name];
    const held = (stored && typeof stored === "object" ? stored[name] : null) || null;
    const raw: any = held && typeof held === "object" ? held : {};
    return {
        size: NUMBER(raw.size, fallback.size ?? 11, 4, 96),
        weight: raw.weight === undefined ? fallback.weight : (NUMBER(raw.weight, 400, 100, 900)),
        italic: raw.italic === undefined ? fallback.italic : !!raw.italic,
        colour: typeof raw.colour === "string" && /^#[0-9a-fA-F]{3,8}$/.test(raw.colour)
            ? raw.colour : fallback.colour,
        spaceBefore: NUMBER(raw.spaceBefore, fallback.spaceBefore ?? 0, 0, 144),
        spaceAfter: NUMBER(raw.spaceAfter, fallback.spaceAfter ?? 0, 0, 144),
        lineHeight: NUMBER(raw.lineHeight, fallback.lineHeight ?? 1.5, 0.5, 4),
        caps: raw.caps === undefined ? fallback.caps : !!raw.caps,
        underlined: raw.underlined === undefined ? fallback.underlined : !!raw.underlined,
    };
}

/** The whole sheet, read out of the document. */
export function stylesOf(stored: any): StyleSheet {
    return Object.fromEntries(STYLE_NAMES.map(name => [name, styleOf(stored, name)])) as StyleSheet;
}

/** Whether a style still says exactly what the defaults say. Drives "Reset" being offered. */
export function isDefault(stored: any, name: StyleName): boolean {
    const mine = styleOf(stored, name);
    const theirs = styleOf(null, name);
    return (Object.keys({ ...mine, ...theirs }) as (keyof ParagraphStyle)[])
        .every(key => mine[key] === theirs[key]);
}

/**
 * The style sheet as CSS custom properties, to hang on the paper.
 *
 * VARIABLES RATHER THAN A STYLESHEET, and this is the decision that makes the feature small. The
 * paper's own rules are written by MUI, which generates class names and a specificity this cannot
 * reliably beat -- a second stylesheet would be a fight settled by `!important` and lost again the
 * next time somebody edits DocumentPaper. A custom property has no specificity at all: the rule
 * stays where it was written and simply reads its number from here.
 */
export function cssVariables(stored: any): Record<string, string> {
    const sheet = stylesOf(stored);
    const out: Record<string, string> = {};
    STYLE_NAMES.forEach((name) => {
        const style = sheet[name];
        out[`--parkour-${name}-size`] = `${style.size}pt`;
        out[`--parkour-${name}-weight`] = String(style.weight ?? 400);
        out[`--parkour-${name}-style`] = style.italic ? "italic" : "normal";
        // A STYLE WITH NO COLOUR WRITES NO VARIABLE, so the rule falls back to the ink the paper
        // already chose. Writing "inherit" instead would take the colour from whatever box the
        // text is in, which is not the same thing and quietly changes the body ink.
        if (style.colour) out[`--parkour-${name}-colour`] = style.colour;
        out[`--parkour-${name}-before`] = `${style.spaceBefore ?? 0}pt`;
        out[`--parkour-${name}-after`] = `${style.spaceAfter ?? 0}pt`;
        out[`--parkour-${name}-leading`] = String(style.lineHeight ?? 1.5);
        out[`--parkour-${name}-caps`] = style.caps ? "uppercase" : "none";
        out[`--parkour-${name}-rule`] = style.underlined ? "1px solid #000000" : "none";
        out[`--parkour-${name}-rule-pad`] = style.underlined ? "2pt" : "0";
    });
    return out;
}

/** Which style a block of this kind wears. */
export function styleFor(nodeType: string, level?: number): StyleName {
    if (nodeType !== "heading") return "normal";
    const at = Math.min(6, Math.max(1, Number(level) || 1));
    return `h${at}` as StyleName;
}

/**
 * The style a captured block is describing — "update Heading 2 to match this".
 *
 * READ FROM THE BLOCK, NOT FROM THE SCREEN. Measuring the rendered element would read back the
 * style that is already applied, so every update would be a no-op that looked like a bug. What is
 * captured is the DIRECT formatting somebody put on the paragraph, falling back to what the style
 * already says where they put none.
 */
export function captured(stored: any, name: StyleName, direct: {
    size?: number | null; bold?: boolean | null; italic?: boolean | null;
    colour?: string | null; spaceBefore?: number | null; spaceAfter?: number | null;
    lineHeight?: number | null;
}): ParagraphStyle {
    const base = styleOf(stored, name);
    const next: ParagraphStyle = { ...base };
    if (direct.size != null) next.size = NUMBER(direct.size, base.size ?? 11, 4, 96);
    if (direct.bold != null) next.weight = direct.bold ? 700 : 400;
    if (direct.italic != null) next.italic = !!direct.italic;
    if (direct.colour != null) next.colour = direct.colour || undefined;
    if (direct.spaceBefore != null) next.spaceBefore = NUMBER(direct.spaceBefore, 0, 0, 144);
    if (direct.spaceAfter != null) next.spaceAfter = NUMBER(direct.spaceAfter, 0, 0, 144);
    if (direct.lineHeight != null) next.lineHeight = NUMBER(direct.lineHeight, 1.5, 0.5, 4);
    return next;
}
